'use client';

import { useState, useEffect } from 'react';
import { organizationApi } from '@/lib/api';
import { getCreateEventPath } from '@/lib/create-event';
import { useAuthStore } from '@/lib/auth-store';
import {
  ORGANIZATION_DASHBOARD_LOAD_TIMEOUT_MS,
  organizationDashboardErrorMessage,
  parseOrganizationDashboardPayload,
  parseOrganizationRecentEvents,
  shouldHoldOrganizationDashboardLoading,
} from '@/lib/organization-dashboard-data';
import {
  OrganizationHomeDashboard,
  type OrganizationDashboardData,
  type RecentEvent,
} from '@/components/organization/OrganizationHomeDashboard';

export default function OrganizationDashboardPage() {
  const { user, _hasHydrated, authChecking, isAuthenticated } = useAuthStore();
  const [data, setData] = useState<OrganizationDashboardData | null>(null);
  const [recentEvents, setRecentEvents] = useState<RecentEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hasUser = Boolean(user || isAuthenticated);
  const holdForAuth = shouldHoldOrganizationDashboardLoading({
    hydrated: _hasHydrated,
    authChecking,
    hasUser,
  });
  const signedOut = _hasHydrated && !hasUser && !authChecking;

  const createEventHref = getCreateEventPath({
    isAuthenticated: true,
    canCreateEvents: true,
    workspace: 'organization',
    organizationId: data?.organization?.id,
  });

  useEffect(() => {
    if (!_hasHydrated || !hasUser) {
      return;
    }

    let cancelled = false;
    let safetyTimer: number | null = window.setTimeout(() => {
      if (cancelled) return;
      setLoading(false);
      setEventsLoading(false);
      setError((prev) => prev ?? 'Dashboard is taking too long to load. Please retry.');
    }, ORGANIZATION_DASHBOARD_LOAD_TIMEOUT_MS);

    const clearSafetyTimer = () => {
      if (safetyTimer !== null) {
        window.clearTimeout(safetyTimer);
        safetyTimer = null;
      }
    };

    organizationApi
      .dashboard()
      .then((res) => {
        if (cancelled) return;
        const payload = parseOrganizationDashboardPayload(res);
        if (payload) {
          setData(payload);
          setError(null);
        } else {
          setError('Organization data not available.');
        }
      })
      .catch((err) => {
        if (!cancelled) setError(organizationDashboardErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) {
          clearSafetyTimer();
          setLoading(false);
        }
      });

    organizationApi
      .events({ per_page: 5, page: 1 })
      .then((res) => {
        if (cancelled) return;
        setRecentEvents(parseOrganizationRecentEvents(res).slice(0, 5));
      })
      .catch(() => {
        if (!cancelled) setRecentEvents([]);
      })
      .finally(() => {
        if (!cancelled) setEventsLoading(false);
      });

    return () => {
      cancelled = true;
      clearSafetyTimer();
    };
  }, [_hasHydrated, hasUser]);

  return (
    <OrganizationHomeDashboard
      userName={user?.name}
      loading={!signedOut && (loading || holdForAuth)}
      eventsLoading={eventsLoading}
      data={data}
      recentEvents={recentEvents}
      createEventHref={createEventHref}
      error={signedOut ? 'You need to be signed in to view this dashboard.' : error}
    />
  );
}
